import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import LoadingProgress from '../components/LoadingProgress'

import {
  fetchSupportTickets,
  fetchSupportTicket,
  closeTicketSupport,
  reviewTicketSupport,
  resolveTicketSupport,
  incidenceTicketSupport,
  repairTicketSupport,
  deleteSupportTicket
} from '../actions/supporttickets'

import ErrorMessage from '../components/ErrorMessage'
import SupportTicketsFilterForm from '../forms/SupportTicketsFilterForm'

import '../styles/SupportTicketAdminView.css'

const STATUS_LABELS = {
  OPEN: 'Abierto',
  IN_REVIEW: 'En Revisión',
  IN_INCIDENCE: 'En Incidencia',
  IN_REPAIR: 'En Reparación',
  RESOLVED: 'Resuelto',
  CLOSED: 'Cerrado',
}

const PRIORITY_LABELS = {
  LOW: 'Consulta general',
  MEDIUM: 'Incidente menor',
  HIGH: 'Avería',
  CRITICAL: 'Avería urgente',
}

const CATEGORY_LABELS = {
  COINS: 'Pago Monedas',
  BILLS: 'Pago Billetes',
  TPV: 'Pago Tarjeta',
  SCREEN_OFF: 'Pantalla Apagada',
  SCREEN_BREAK: 'Pantalla Rota',
  SCREEN_TOUCH: 'Táctil No Responde',
  PRINTER_TICKET: 'Impresora Ticket',
  KIOSC_DOOR: 'Kiosco Puerta',
  KIOSC_OFF: 'Kiosco No Enciende',
  OTHER: 'Otro',
}

const FILTER_TABS = [
  { key: 'ALL', label: 'Todos' },
  { key: 'OPEN', label: 'Abiertos' },
  { key: 'IN_REVIEW', label: 'En Revisión' },
  { key: 'IN_INCIDENCE', label: 'En Incidencia' },
  { key: 'IN_REPAIR', label: 'En Reparación' },
  { key: 'RESOLVED', label: 'Resueltos' },
  { key: 'CLOSED', label: 'Cerrados' },
]

class SupportTicketsView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      activeFilter: 'ALL',
      closeConfirmShown: false,
      closeId: null,
      detailModalOpen: false,
      detailId: null,
      deleteConfirmShown: false,
      deleteId: null,
      actuationText: '',
      showActuationModal: false,
      actuationAction: null, // 'resolve' or 'incidence'
      reviewPriority: '', // Priority selected for review action
      now: new Date(), // for live time elapsed updates
    }

    this.handleDownload = this.handleDownload.bind(this)
    this.confirmClose = this.confirmClose.bind(this)
    this.confirmDelete = this.confirmDelete.bind(this)
    this.closeDetailModal = this.closeDetailModal.bind(this)
    this.handleActuationSubmit = this.handleActuationSubmit.bind(this)
  }

  componentDidMount() {
    this.props.fetchSupportTickets('', '', '', '', '', '')
    // fetch SLAs once (single call) and reuse in KPI calculation — avoids populating SLA per ticket
    if (this.props.fetchSLAs) this.props.fetchSLAs()
    // Update time every 60 seconds for live elapsed time
    this._timer = setInterval(() => this.setState({ now: new Date() }), 60000)
  }

  componentWillUnmount() {
    if (this._timer) clearInterval(this._timer)
  }

  componentDidUpdate(prevProps) {
    if (this.props.supportticketNeedRefresh && !prevProps.supportticketNeedRefresh) {
      this.props.fetchSupportTickets('', '', '', '', '', '')
      // also refresh the open detail if any
      if (this.state.detailId) {
        this.props.fetchSupportTicket(this.state.detailId)
      }
    }
  }

  getStatusCounts() {
    const { supporttickets } = this.props
    if (!supporttickets) return {}
    return supporttickets.reduce((acc, t) => {
      acc[t.Status] = (acc[t.Status] || 0) + 1
      return acc
    }, {})
  }

  /**
   * Format a millisecond duration into a human-readable string.
   * Minutes are always rounded UP to the next whole minute (ceil).
   */
  formatDuration(ms) {
    if (ms <= 0) return '0m'
    // round any partial minute up, then split into days/hours/minutes so
    // carries (60 minutes → 1 hour) are handled automatically
    const totalMinutes = Math.ceil(ms / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60
    let text = ''
    if (days > 0) text += `${days}d `
    if (hours > 0 || days > 0) text += `${hours}h `
    text += `${minutes}m`
    return text.trim()
  }

  /**
   * Get severity class based on elapsed days
   */
  getTimerSeverity(ms) {
    const days = ms / (1000 * 60 * 60 * 24)
    if (days >= 3) return 'critical'
    if (days >= 1) return 'warning'
    return 'normal'
  }

  /**
   * Compute SLA remaining milliseconds for a ticket.
   * - Uses customer's SLA id + cached SLAs in props (no per-ticket SLA populate required)
   * - Response time = ReviewedAt - OpenAt (or now - OpenAt if not reviewed yet)
   * - remainingMs = slaMaxHours*3600*1000 - responseMs
   * Returns null if SLA or timestamps are missing.
   */
  getSlaRemainingMs(ticket) {
    if (!ticket || !ticket.OpenAt || !this.props.slas || !ticket.Customer || !ticket.Customer.SLA) return null

    // find SLA object by id (support populated or raw id)
    const slaId = ticket.Customer.SLA && (ticket.Customer.SLA._id || ticket.Customer.SLA)
    const slaObj = (this.props.slas || []).find(s => String(s._id) === String(slaId))
    if (!slaObj) return null

    const priorityKey = (ticket.Priority || 'MEDIUM').toLowerCase()
    const slaEntry = slaObj[priorityKey]
    const slaMax = slaEntry && slaEntry.maxResponseTime
    if (typeof slaMax !== 'number') return null

    const openAt = new Date(ticket.OpenAt)
    // use the ticket's "total" end timestamp:
    // - if the ticket is finished use the final timestamp, otherwise use the live `now`
    const lastTs = ticket.ClosedAt || ticket.ResolvedAt || ticket.InRepairAt || ticket.InIncidenceAt || this.state.now || new Date()
    const totalMs = lastTs && openAt ? (new Date(lastTs) - openAt) : 0
    if (isNaN(totalMs) || totalMs < 0) return null

    const remainingMs = (slaMax * 60 * 60 * 1000) - totalMs
    return remainingMs
  }

  /**
   * Calculate time counters for each status transition.
   * Returns an array of { key, icon, label, time, active, severity }
   * - active: true if the ticket has reached/passed this status
   * - time: formatted duration between the two timestamps
   *
   * Workflow: OPEN → IN_REVIEW → RESOLVED | IN_INCIDENCE → IN_REPAIR → CLOSED
   */
  getStatusTimers(ticket) {
    const now = this.state.now
    const openAt = ticket.OpenAt ? new Date(ticket.OpenAt) : null
    const reviewedAt = ticket.ReviewedAt ? new Date(ticket.ReviewedAt) : null
    const resolvedAt = ticket.ResolvedAt ? new Date(ticket.ResolvedAt) : null
    const inIncidenceAt = ticket.InIncidenceAt ? new Date(ticket.InIncidenceAt) : null
    const inRepairAt = ticket.InRepairAt ? new Date(ticket.InRepairAt) : null
    const closedAt = ticket.ClosedAt ? new Date(ticket.ClosedAt) : null

    const STATUS_ORDER = ['OPEN', 'IN_REVIEW', 'IN_INCIDENCE', 'IN_REPAIR', 'RESOLVED', 'CLOSED']
    const currentIdx = STATUS_ORDER.indexOf(ticket.Status)

    const timers = []

    // 1) Apertura → Revisión
    const t1End = reviewedAt || (ticket.Status === 'OPEN' ? now : null)
    const t1Ms = openAt && t1End ? t1End - openAt : 0
    const t1Active = currentIdx >= 0 // always active since always open
    timers.push({
      key: 'open_to_review',
      icon: 'fas fa-folder-open',
      label: 'Abierto',
      time: this.formatDuration(Math.max(t1Ms, 0)),
      ms: Math.max(t1Ms, 0),
      active: t1Active,
      isLive: ticket.Status === 'OPEN',
    })

    // 2) Revisión → Resolución/Incidencia
    const t2Start = reviewedAt
    const t2End = resolvedAt || inIncidenceAt || (ticket.Status === 'IN_REVIEW' ? now : null)
    const t2Ms = t2Start && t2End ? t2End - t2Start : 0
    const t2Active = currentIdx >= 1 // IN_REVIEW or beyond
    timers.push({
      key: 'review_to_next',
      icon: 'fas fa-eye',
      label: 'En Revisión',
      time: this.formatDuration(Math.max(t2Ms, 0)),
      ms: Math.max(t2Ms, 0),
      active: t2Active,
      isLive: ticket.Status === 'IN_REVIEW',
    })

    // 3) Incidencia → Reparación/Resolución
    const t3Start = inIncidenceAt
    const t3End = inRepairAt || resolvedAt || (ticket.Status === 'IN_INCIDENCE' ? now : null)
    const t3Ms = t3Start && t3End ? t3End - t3Start : 0
    const t3Active = !!inIncidenceAt
    timers.push({
      key: 'incidence',
      icon: 'fas fa-exclamation-triangle',
      label: 'En Incidencia',
      time: this.formatDuration(Math.max(t3Ms, 0)),
      ms: Math.max(t3Ms, 0),
      active: t3Active,
      isLive: ticket.Status === 'IN_INCIDENCE',
    })

    // 4) Reparación
    const t4Start = inRepairAt
    const t4End = resolvedAt || (ticket.Status === 'IN_REPAIR' ? now : null)
    const t4Ms = t4Start && t4End ? t4End - t4Start : 0
    const t4Active = !!inRepairAt
    timers.push({
      key: 'repair',
      icon: 'fas fa-wrench',
      label: 'En Reparación',
      time: this.formatDuration(Math.max(t4Ms, 0)),
      ms: Math.max(t4Ms, 0),
      active: t4Active,
      isLive: ticket.Status === 'IN_REPAIR',
    })

    // 5) Resolución → Cierre
    const t5Start = resolvedAt
    const t5End = closedAt || (ticket.Status === 'RESOLVED' ? now : null)
    const t5Ms = t5Start && t5End ? t5End - t5Start : 0
    const t5Active = !!resolvedAt
    timers.push({
      key: 'resolved',
      icon: 'fas fa-check-circle',
      label: 'Resuelto',
      time: this.formatDuration(Math.max(t5Ms, 0)),
      ms: Math.max(t5Ms, 0),
      active: t5Active,
      isLive: ticket.Status === 'RESOLVED',
    })

    // 6) Total (OpenAt → last known timestamp or now)
    const lastTs = closedAt || resolvedAt || inRepairAt || inIncidenceAt || reviewedAt || now
    const totalMs = openAt ? lastTs - openAt : 0
    timers.push({
      key: 'total',
      icon: 'fas fa-hourglass-half',
      label: 'Total',
      time: this.formatDuration(Math.max(totalMs, 0)),
      ms: Math.max(totalMs, 0),
      active: true,
      isLive: ticket.Status !== 'CLOSED',
      isTotal: true,
    })

    return timers
  }

  /**
   * Calculate simplified time counters for SAT1 users.
   * Returns only 3 timers:
   * 1. Open time: from OPEN to IN_REVIEW (or now if still open)
   * 2. In revision time: from IN_REVIEW to RESOLVED/IN_INCIDENCE (or now if still in review)
   * 3. Total: sum of both counters
   */
  getSAT1Timers(ticket) {
    const now = this.state.now
    const openAt = ticket.OpenAt ? new Date(ticket.OpenAt) : null
    const reviewedAt = ticket.ReviewedAt ? new Date(ticket.ReviewedAt) : null
    const resolvedAt = ticket.ResolvedAt ? new Date(ticket.ResolvedAt) : null
    const inIncidenceAt = ticket.InIncidenceAt ? new Date(ticket.InIncidenceAt) : null

    const timers = []

    // 1) Open Time: OPEN → IN_REVIEW (or now if still open)
    const openEnd = reviewedAt || (ticket.Status === 'OPEN' ? now : null)
    const openMs = openAt && openEnd ? openEnd - openAt : 0
    timers.push({
      key: 'sat1_open',
      icon: 'fas fa-folder-open',
      label: 'Tiempo Abierto',
      time: this.formatDuration(Math.max(openMs, 0)),
      ms: Math.max(openMs, 0),
      active: true,
      isLive: ticket.Status === 'OPEN',
      severity: this.getTimerSeverity(Math.max(openMs, 0)),
    })

    // 2) In Revision Time: IN_REVIEW → RESOLVED/IN_INCIDENCE (or now if still in review)
    const revisionStart = reviewedAt
    const revisionEnd = resolvedAt || inIncidenceAt || (ticket.Status === 'IN_REVIEW' ? now : null)
    const revisionMs = revisionStart && revisionEnd ? revisionEnd - revisionStart : 0
    const revisionActive = !!reviewedAt
    timers.push({
      key: 'sat1_revision',
      icon: 'fas fa-eye',
      label: 'Tiempo en Revisión',
      time: this.formatDuration(Math.max(revisionMs, 0)),
      ms: Math.max(revisionMs, 0),
      active: revisionActive,
      isLive: ticket.Status === 'IN_REVIEW',
      severity: revisionActive ? this.getTimerSeverity(Math.max(revisionMs, 0)) : 'normal',
    })

    // 3) Total: sum of open time + revision time
    const totalMs = Math.max(openMs, 0) + Math.max(revisionMs, 0)
    timers.push({
      key: 'sat1_total',
      icon: 'fas fa-hourglass-half',
      label: 'Tiempo Total',
      time: this.formatDuration(totalMs),
      ms: totalMs,
      active: true,
      isLive: ticket.Status === 'OPEN' || ticket.Status === 'IN_REVIEW',
      isTotal: true,
      severity: this.getTimerSeverity(totalMs),
    })

    return timers
  }

  getCategoryCounts() {
    const { supporttickets } = this.props
    if (!supporttickets) return {}
    return supporttickets.reduce((acc, t) => {
      acc[t.Category] = (acc[t.Category] || 0) + 1
      return acc
    }, {})
  }

  getPriorityCounts() {
    const { supporttickets } = this.props
    if (!supporttickets) return {}
    return supporttickets.reduce((acc, t) => {
      acc[t.Priority] = (acc[t.Priority] || 0) + 1
      return acc
    }, {})
  }

  getKPIs() {
    const { supporttickets } = this.props
    if (!supporttickets) return { pendingCount: 0, resolvedToday: 0, avgResponseTime: 0, avgResolutionTime: 0, criticalOpen: 0 }

    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    let pendingCount = 0
    let resolvedToday = 0
    let criticalOpen = 0
    let totalResponseTime = 0
    let responseTimeCount = 0
    let totalResolutionTime = 0
    let resolutionTimeCount = 0

    // SLA compliance counters
    let slaConsideredCount = 0
    let slaPassCount = 0

    supporttickets.forEach(t => {
      // Pending: only OPEN (not reviewed) or IN_REVIEW (reviewed but not resolved/escalated)
      if (t.Status === 'OPEN' || t.Status === 'IN_REVIEW') {
        pendingCount++
      }

      // Critical open: high/critical priority AND not resolved/closed
      if ((t.Priority === 'CRITICAL' || t.Priority === 'HIGH') && t.Status !== 'RESOLVED' && t.Status !== 'CLOSED') {
        criticalOpen++
      }

      // Resolved today
      if ((t.Status === 'RESOLVED' || t.Status === 'CLOSED') && t.ResolvedAt) {
        if (new Date(t.ResolvedAt) >= todayStart) {
          resolvedToday++
        }
      }

      // Avg response time: OpenAt → ReviewedAt
      if (t.ReviewedAt && t.OpenAt) {
        const diffH = (new Date(t.ReviewedAt) - new Date(t.OpenAt)) / (1000 * 60 * 60)
        if (diffH > 0 && diffH < 2000) {
          totalResponseTime += diffH
          responseTimeCount++
        }
      }

      // Avg resolution time: OpenAt → ResolvedAt
      if (t.ResolvedAt && t.OpenAt) {
        const diffH = (new Date(t.ResolvedAt) - new Date(t.OpenAt)) / (1000 * 60 * 60)
        if (diffH > 0 && diffH < 5000) {
          totalResolutionTime += diffH
          resolutionTimeCount++
        }
      }

      // SLA compliance: only consider tickets that are RESOLVED or IN_INCIDENCE
      if ((t.Status === 'RESOLVED' || t.Status === 'IN_INCIDENCE') && t.OpenAt && t.ReviewedAt && t.Customer && t.Customer.SLA && this.props.slas) {
        // customer.SLA is an ObjectId (we avoid populating the full SLA on the tickets endpoint)
        const slaId = t.Customer.SLA && (t.Customer.SLA._id || t.Customer.SLA) // support both populated and non-populated shapes
        const slaObj = (this.props.slas || []).find(s => s._id === slaId || String(s._id) === String(slaId))
        if (slaObj) {
          const priority = (t.Priority || 'MEDIUM').toLowerCase()
          const slaEntry = slaObj[priority]
          const slaMax = slaEntry && slaEntry.maxResponseTime

          if (typeof slaMax === 'number') {
            const responseH = (new Date(t.ReviewedAt) - new Date(t.OpenAt)) / (1000 * 60 * 60)
            // count only sensible response times
            if (responseH >= 0 && responseH < 2000) {
              slaConsideredCount++
              if (responseH <= slaMax) slaPassCount++
            }
          }
        }
      }
    })

    return {
      pendingCount,
      resolvedToday,
      // keep previous averages for backward compatibility
      avgResponseTime: responseTimeCount > 0 ? Math.round(totalResponseTime / responseTimeCount * 10) / 10 : 0,
      avgResolutionTime: resolutionTimeCount > 0 ? Math.round(totalResolutionTime / resolutionTimeCount * 10) / 10 : 0,
      // SLA compliance ratio: percentage of tickets that met SLA (considered = resolved or in_incidence and have SLA)
      slaComplianceRatio: slaConsideredCount > 0 ? Math.round((slaPassCount / slaConsideredCount) * 1000) / 10 : null,
      slaConsideredCount,
      slaPassCount,
      criticalOpen,
    }
  }

  getFilteredTickets() {
    const { supporttickets } = this.props
    const { activeFilter } = this.state
    if (!supporttickets) return []
    if (activeFilter === 'ALL') return supporttickets
    return supporttickets.filter(t => t.Status === activeFilter)
  }

  handleDownload() {
    const { supporttickets } = this.props
    if (!supporttickets || supporttickets.length === 0) return

    const headers = ['#', 'Fecha Apertura', 'Fecha Cierre', 'Cliente', 'Máquina', 'Categoría', 'Descripción', 'Actuación', 'Estado', 'Prioridad', 'Ejecutor', 'Cerrado por']
    const rows = supporttickets.map(t => [
      t.number,
      new Date(t.OpenAt).toLocaleString(),
      t.ClosedAt ? new Date(t.ClosedAt).toLocaleString() : '',
      t.Customer && t.Customer.Fullname ? t.Customer.Fullname : 'VACWAY',
      t.Machine ? t.Machine.Serial : 'Todas las máquinas',
      t.Category,
      t.Description ? t.Description.replace(/[\n\r,;]/g, ' ') : '',
      t.Actuation ? t.Actuation.replace(/[\n\r,;]/g, ' ') : '',
      t.Status,
      t.Priority,
      t.Executor ? t.Executor.fullname : '',
      t.ClosedBy ? t.ClosedBy.fullname : ''
    ])

    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${cell}"`).join(';')
    ).join('\n')

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `tickets_soporte_${new Date().toISOString().substring(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  goTo(id) {
    this.setState({ detailModalOpen: true, detailId: id })
    this.props.fetchSupportTicket(id)
  }

  closeDetailModal() {
    this.setState({ detailModalOpen: false, detailId: null, actuationText: '', reviewPriority: '' })
  }

  handleReviewTicket(id, e) {
    if (e) e.stopPropagation()
    const { reviewPriority } = this.state
    if (!reviewPriority) return
    this.props.reviewTicketSupport(id, reviewPriority)
    this.setState({ reviewPriority: '' })
  }

  // Open actuation modal for resolve action
  handleOpenResolveModal(id, e) {
    if (e) e.stopPropagation()
    this.setState({ 
      showActuationModal: true, 
      actuationAction: 'resolve',
      actuationText: ''
    })
  }

  // Open actuation modal for incidence action
  handleOpenIncidenceModal(id, e) {
    if (e) e.stopPropagation()
    this.setState({ 
      showActuationModal: true, 
      actuationAction: 'incidence',
      actuationText: ''
    })
  }

  handleActuationSubmit() {
    const { actuationAction, actuationText, detailId } = this.state
    
    if (!actuationText.trim()) {
      alert('Por favor, introduce el texto de actuación')
      return
    }

    if (actuationAction === 'resolve') {
      this.props.resolveTicketSupport(detailId, actuationText)
    } else if (actuationAction === 'incidence') {
      this.props.incidenceTicketSupport(detailId, actuationText)
    }

    this.setState({ 
      showActuationModal: false, 
      actuationAction: null, 
      actuationText: '' 
    })
  }

  handleRepairTicket(id, e) {
    if (e) e.stopPropagation()
    this.props.repairTicketSupport(id)
  }

  handleCloseTicket(id, e) {
    if (e) e.stopPropagation()
    this.setState({ closeConfirmShown: true, closeId: id })
  }

  confirmClose() {
    this.setState({ closeConfirmShown: false })
    this.props.closeTicketSupport(this.state.closeId)
  }

  handleDeleteTicket(id, e) {
    if (e) e.stopPropagation()
    this.setState({ deleteConfirmShown: true, deleteId: id })
  }

  confirmDelete() {
    const id = this.state.deleteId
    this.setState({ deleteConfirmShown: false, deleteId: null })
    this.props.deleteSupportTicket(id)
    // close detail modal if open
    if (this.state.detailId === id) {
      this.closeDetailModal()
    }
  }

  renderTicketCard(ticket) {
    const statusLabel = STATUS_LABELS[ticket.Status] || ticket.Status
    const priorityLabel = PRIORITY_LABELS[ticket.Priority] || ticket.Priority
    const categoryLabel = CATEGORY_LABELS[ticket.Category] || ticket.Category
    // Use simplified 3-timer view for all users
    const timers = this.getSAT1Timers(ticket)

    return (
      <div className="st-admin-ticket-card" key={ticket._id}
        onClick={() => this.goTo(ticket._id)}>
        <div className="st-admin-ticket-row">
          <div className={`st-admin-status-bar ${ticket.Status}`}></div>
          <div className="st-admin-ticket-content">
            <div className="st-admin-ticket-info">
              <div className="st-admin-ticket-number">#{ticket.number}</div>
              <div className="st-admin-ticket-meta">
                <span><i className="fas fa-calendar-alt"></i>
                  {new Date(ticket.OpenAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <span><i className="fas fa-tag"></i>{categoryLabel}</span>
              </div>
            </div>

            <div className="st-admin-ticket-context">
              <div className="st-admin-context-item">
                <i className="fas fa-building"></i>
                <strong>{ticket.Customer && ticket.Customer.Fullname ? ticket.Customer.Fullname : 'VACWAY'}</strong>
              </div>
              <div className="st-admin-context-item">
                <i className="fas fa-hdd"></i>
                {ticket.Machine && ticket.Machine.Serial ? ticket.Machine.Serial : 'Todas las máquinas'}
              </div>
              {ticket.Executor &&
                <div className="st-admin-context-item">
                  <i className="fas fa-user-cog"></i>
                  {ticket.Executor.fullname}
                </div>
              }
            </div>

            <div className="st-admin-ticket-desc">
              {ticket.Description
                ? <span className="desc-text">{ticket.Description}</span>
                : <span className="desc-empty">Sin descripción</span>
              }
            </div>

            {/* Status & Priority badges */}
            <div className="st-admin-ticket-badges">
              <span className={`ticket-status-badge ${ticket.Status}`}>
                {statusLabel}
              </span>
              <span className={`ticket-priority-badge ${ticket.Priority}`}>
                {priorityLabel}
              </span>
            </div>

            {/* Time counters column */}
            <div className="st-admin-ticket-timers">
              {timers.map(t => (
                <div key={t.key}
                  className={`st-timer-chip ${t.active ? (t.severity || this.getTimerSeverity(t.ms)) : 'disabled'} ${t.isLive ? 'live' : ''} ${t.isTotal ? 'total' : ''}`}>
                  <i className={t.icon}></i>
                  <span className="st-timer-value">{t.active ? t.time : '--'}</span>
                </div>
              ))}

              {/* SLA remaining counter */}
              {(() => {
                const slaRemMs = this.getSlaRemainingMs(ticket)
                if (slaRemMs === null) return null
                const isPositive = slaRemMs > 0
                const cls = `st-timer-chip ${isPositive ? 'normal' : 'critical'}`
                const display = isPositive ? this.formatDuration(slaRemMs) : `-${this.formatDuration(Math.abs(slaRemMs))}`
                return (
                  <div key="sla_rem" className={cls} title={isPositive ? 'Tiempo restante antes de SLA' : 'Tiempo pasado del SLA'}>
                    <i className="fas fa-stopwatch"></i>
                    <span className="st-timer-value">{display}</span>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderDetailModal() {
    const { supportticket, role, type } = this.props
    const { detailModalOpen, showActuationModal, actuationAction, actuationText, reviewPriority } = this.state

    if (!detailModalOpen) return null

    const isLoading = !supportticket
    const isAdmin = role === 'ADMIN'
    const isMaster = type === 'MASTER'
    const isClosed = supportticket && supportticket.Status === 'CLOSED'

    return (
      <>
        <Modal isOpen={detailModalOpen} toggle={this.closeDetailModal}
          className="st-detail-modal" size="xl">
          <ModalHeader toggle={this.closeDetailModal} className="st-detail-modal-header">
            {supportticket
              ? <span><i className="fas fa-ticket-alt"></i> Ticket #{supportticket.number}</span>
              : <span><i className="fas fa-ticket-alt"></i> Cargando...</span>
            }
          </ModalHeader>
          <ModalBody className="st-detail-modal-body">
            {isLoading ? <LoadingProgress /> : (
              <>
                {/* Status strip: badges + timers in one row */}
                <div className="st-modal-status-strip">
                  <div className="st-modal-strip-badges">
                    <span className={`ticket-status-badge ${supportticket.Status}`}>
                      <i className="fas fa-circle"></i>
                      {STATUS_LABELS[supportticket.Status] || supportticket.Status}
                    </span>
                    <span className={`ticket-priority-badge ${supportticket.Priority}`}>
                      <i className="fas fa-flag"></i>
                      {PRIORITY_LABELS[supportticket.Priority] || supportticket.Priority}
                    </span>
                  </div>
                  <div className="st-modal-strip-separator"></div>
                  <div className="st-modal-strip-timers">
                    {this.getSAT1Timers(supportticket).map(t => (
                      <div key={t.key}
                        className={`st-modal-timer-item ${t.active ? (t.severity || this.getTimerSeverity(t.ms)) : 'disabled'} ${t.isLive ? 'live' : ''} ${t.isTotal ? 'total' : ''}`}>
                        <div className="st-modal-timer-icon"><i className={t.icon}></i></div>
                        <div className="st-modal-timer-info">
                          <div className="st-modal-timer-label">{t.label}</div>
                          <div className="st-modal-timer-value">{t.active ? t.time : '--'}</div>
                        </div>
                      </div>
                    ))}

                    {/* SLA remaining (modal) */}
                    {(() => {
                      const slaRemMs = this.getSlaRemainingMs(supportticket)
                      if (slaRemMs === null) return null
                      const isPositive = slaRemMs > 0
                      const label = isPositive ? 'Tiempo hasta SLA' : 'Tiempo sobre SLA'
                      const value = isPositive ? this.formatDuration(slaRemMs) : `-${this.formatDuration(Math.abs(slaRemMs))}`
                      const severity = isPositive ? 'normal' : 'critical'
                      return (
                        <div className={`st-modal-timer-item ${severity}`} key="sla_modal">
                          <div className="st-modal-timer-icon"><i className="fas fa-stopwatch"></i></div>
                          <div className="st-modal-timer-info">
                            <div className="st-modal-timer-label">{label}</div>
                            <div className="st-modal-timer-value">{value}</div>
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                </div>

                {/* Info grid */}
                <div className="st-modal-detail-grid">
                  <div className="st-detail-card">
                    <div className="st-detail-card-title">
                      <i className="fas fa-info-circle"></i> Información General
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Número</span>
                        <span className="st-detail-field-value">#{supportticket.number}</span>
                      </div>
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Categoría</span>
                        <span className="st-detail-field-value">{CATEGORY_LABELS[supportticket.Category] || supportticket.Category}</span>
                      </div>
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Cliente</span>
                        <span className="st-detail-field-value">{supportticket.Customer && supportticket.Customer.Fullname ? supportticket.Customer.Fullname : 'VACWAY'}</span>
                      </div>
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Máquina</span>
                        <span className="st-detail-field-value">{supportticket.Machine && supportticket.Machine.Serial ? supportticket.Machine.Serial : 'Todas las máquinas'}</span>
                      </div>
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Creado por</span>
                        <span className="st-detail-field-value">{supportticket.User ? supportticket.User.fullname : supportticket.Customer.Fullname}</span>
                      </div>
                    </div>
                  </div>

                  <div className="st-detail-card">
                    <div className="st-detail-card-title">
                      <i className="fas fa-calendar"></i> Fechas y Asignaciones
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Fecha Apertura</span>
                        <span className="st-detail-field-value">{new Date(supportticket.OpenAt).toLocaleString()}</span>
                      </div>
                      {supportticket.Executor &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">Ejecutor</span>
                          <span className="st-detail-field-value">{supportticket.Executor.fullname}</span>
                        </div>
                      }
                      {supportticket.ReviewedAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">En Revisión</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.ReviewedAt).toLocaleString()}
                            {supportticket.ReviewedBy ? ` — ${supportticket.ReviewedBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {supportticket.InIncidenceAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">En Incidencia</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.InIncidenceAt).toLocaleString()}
                            {supportticket.InIncidenceBy ? ` — ${supportticket.InIncidenceBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {supportticket.InRepairAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">En Reparación</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.InRepairAt).toLocaleString()}
                            {supportticket.InRepairBy ? ` — ${supportticket.InRepairBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {supportticket.ResolvedAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">Resuelto</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.ResolvedAt).toLocaleString()}
                            {supportticket.ResolvedBy ? ` — ${supportticket.ResolvedBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {supportticket.ClosedAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">Cerrado</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.ClosedAt).toLocaleString()}
                            {supportticket.ClosedBy ? ` — ${supportticket.ClosedBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="st-detail-card" style={{ marginBottom: 16 }}>
                  <div className="st-detail-card-title">
                    <i className="fas fa-file-alt"></i> Descripción
                  </div>
                  <div className="st-detail-card-body">
                    <div className="st-detail-description">
                      {supportticket.Description || 'Sin descripción'}
                    </div>
                  </div>
                </div>

                {/* Incidence comment */}
                {supportticket.InIncidenceAt && supportticket.Actuation &&
                  <div className="st-detail-card" style={{ marginBottom: 16 }}>
                    <div className="st-detail-card-title">
                      <i className="fas fa-exclamation-triangle"></i> Motivo de Incidencia
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-description">
                        {supportticket.Actuation}
                      </div>
                    </div>
                  </div>
                }

                {/* Actuation */}
                {supportticket.Actuation &&
                  <div className="st-detail-card" style={{ marginBottom: 16 }}>
                    <div className="st-detail-card-title">
                      <i className="fas fa-tools"></i> Actuación Realizada
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-description">
                        {supportticket.Actuation}
                      </div>
                    </div>
                  </div>
                }

                {supportticket.Observations &&
                  <div className="st-detail-card" style={{ marginBottom: 16 }}>
                    <div className="st-detail-card-title">
                      <i className="fas fa-sticky-note"></i> Observaciones
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-description">
                        {supportticket.Observations}
                      </div>
                    </div>
                  </div>
                }

                {/* Actions - New workflow */}
                {isAdmin && !isClosed &&
                  <div className="st-detail-actions-bar">
                    <h3><i className="fas fa-cogs"></i> Acciones</h3>
                    <div className="st-detail-actions-row justify-content-center">
                      {/* OPEN -> Priority select + Review button (disabled until priority selected) */}
                      {supportticket.Status === 'OPEN' &&
                        <div className="st-review-action-group">
                          <select
                            className="st-review-priority-select"
                            value={reviewPriority}
                            onChange={e => this.setState({ reviewPriority: e.target.value })}
                          >
                            <option value="">Seleccionar prioridad...</option>
                            <option value="LOW">{PRIORITY_LABELS.LOW}</option>
                            <option value="MEDIUM">{PRIORITY_LABELS.MEDIUM}</option>
                            <option value="HIGH">{PRIORITY_LABELS.HIGH}</option>
                            <option value="CRITICAL">{PRIORITY_LABELS.CRITICAL}</option>
                          </select>
                          <button
                            className="st-detail-action-btn review"
                            disabled={!reviewPriority}
                            onClick={() => this.handleReviewTicket(supportticket._id)}
                          >
                            <i className="fas fa-eye"></i>Revisar
                          </button>
                        </div>
                      }
                      {/* IN_REVIEW -> Resolve or Incidence */}
                      {supportticket.Status === 'IN_REVIEW' &&
                        <>
                          <button className="st-detail-action-btn resolve"
                            onClick={() => this.handleOpenResolveModal(supportticket._id)}>
                            <i className="fas fa-check"></i>Resolver
                          </button>
                          <button className="st-detail-action-btn incidence"
                            onClick={() => this.handleOpenIncidenceModal(supportticket._id)}>
                            <i className="fas fa-exclamation-triangle"></i>Poner en Incidencia
                          </button>
                        </>
                      }
                      {/* IN_INCIDENCE -> No repair button (managed through incidences view) */}
                      {/* Close button - only for MASTER */}
                      {isMaster &&
                        <button className="st-detail-action-btn close-action"
                          onClick={() => this.handleCloseTicket(supportticket._id)}>
                          <i className="fas fa-times"></i>Cerrar
                        </button>
                      }
                    </div>
                  </div>
                }

                {/* Delete button - only for ADMIN MASTER */}
                {isAdmin && isMaster &&
                  <div className="st-detail-delete-row justify-content-center">
                    <button className="st-detail-delete-btn"
                      onClick={() => this.handleDeleteTicket(supportticket._id)}>
                      <i className="fas fa-trash"></i>Eliminar Ticket
                    </button>
                  </div>
                }
              </>
            )}
          </ModalBody>
        </Modal>

        {/* Actuation Modal for Resolve/Incidence */}
        <Modal isOpen={showActuationModal} toggle={() => this.setState({ showActuationModal: false, actuationText: '' })}>
          <ModalHeader toggle={() => this.setState({ showActuationModal: false, actuationText: '' })}>
            {actuationAction === 'resolve' ? 'Resolver Ticket' : 'Poner en Incidencia'}
          </ModalHeader>
          <ModalBody>
            <p>
              {actuationAction === 'resolve' 
                ? 'Describe la actuación realizada para resolver el ticket:' 
                : 'Describe el motivo por el que se pone en incidencia:'}
            </p>
            <textarea
              className="form-control"
              rows={4}
              value={actuationText}
              onChange={(e) => this.setState({ actuationText: e.target.value })}
              placeholder="Escribe la actuación realizada..."
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.handleActuationSubmit}>
              {actuationAction === 'resolve' ? 'Resolver' : 'Poner en Incidencia'}
            </Button>
            <Button color="secondary" onClick={() => this.setState({ showActuationModal: false, actuationText: '' })}>
              Cancelar
            </Button>
          </ModalFooter>
        </Modal>
      </>
    )
  }

  renderSummaryPanel() {
    const { supporttickets } = this.props
    const counts = this.getStatusCounts()
    const categoryCounts = this.getCategoryCounts()
    const priorityCounts = this.getPriorityCounts()
    const total = supporttickets ? supporttickets.length : 0

    return (
      <div className="st-admin-summary-panel">
        {/* Status summary */}
        <div className="st-summary-card">
          <div className="st-summary-card-header">
            <h3><i className="fas fa-chart-pie"></i> Resumen por Estado</h3>
          </div>
          <div className="st-summary-card-body">
            <div className="st-summary-total">{total} tickets totales</div>
            <div className="st-summary-table">
              {['OPEN', 'IN_REVIEW', 'IN_INCIDENCE', 'IN_REPAIR', 'RESOLVED', 'CLOSED'].map(status => {
                const count = counts[status] || 0
                const pct = total > 0 ? Math.round((count / total) * 100) : 0
                return (
                  <div className="st-summary-row" key={status}
                    onClick={() => this.setState({ activeFilter: status })}>
                    <div className="st-summary-row-left">
                      <span className={`st-summary-dot ${status}`}></span>
                      <span className="st-summary-label">{STATUS_LABELS[status]}</span>
                    </div>
                    <div className="st-summary-row-right">
                      <span className="st-summary-count">{count}</span>
                      <div className="st-summary-bar-bg">
                        <div className={`st-summary-bar-fill ${status}`} style={{ width: `${pct}%` }}></div>
                      </div>
                      <span className="st-summary-pct">{pct}%</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Priority summary */}
        <div className="st-summary-card">
          <div className="st-summary-card-header">
            <h3><i className="fas fa-exclamation-triangle"></i> Por Prioridad</h3>
          </div>
          <div className="st-summary-card-body">
            <div className="st-summary-table">
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(priority => {
                const count = priorityCounts[priority] || 0
                return (
                  <div className="st-summary-row" key={priority}>
                    <div className="st-summary-row-left">
                      <span className={`ticket-priority-badge ${priority}`} style={{ fontSize: 10 }}>
                        {PRIORITY_LABELS[priority]}
                      </span>
                    </div>
                    <div className="st-summary-row-right">
                      <span className="st-summary-count">{count}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Category summary */}
        <div className="st-summary-card">
          <div className="st-summary-card-header">
            <h3><i className="fas fa-tags"></i> Por Categoría</h3>
          </div>
          <div className="st-summary-card-body">
            <div className="st-summary-table">
              {Object.entries(categoryCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, count]) => (
                  <div className="st-summary-row" key={cat}>
                    <div className="st-summary-row-left">
                      <span className="st-summary-label">{CATEGORY_LABELS[cat] || cat}</span>
                    </div>
                    <div className="st-summary-row-right">
                      <span className="st-summary-count">{count}</span>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { role, supporttickets, error, loading } = this.props
    const { activeFilter } = this.state
    const counts = this.getStatusCounts()
    const filteredTickets = this.getFilteredTickets()
    const kpis = this.getKPIs()

    return (
      <div className="support-ticket-admin-view">
        {/* Header */}
        <div className="st-admin-header">
          <div className="header-content">
            <div className="header-text">
              <h1>
                <i className="fas fa-headset mr-3"></i>
                Tickets de Soporte
              </h1>
            </div>
            <div className="header-actions">
              {role === 'ADMIN' &&
                <button className="st-admin-download-btn" onClick={this.handleDownload}>
                  <i className="fas fa-file-excel"></i>Descargar CSV
                </button>
              }
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        {supporttickets && !loading && (
          <div className="st-kpi-row">
            <div className="st-kpi-card pending">
              <div className="kpi-icon"><i className="fas fa-clock"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.pendingCount}</div>
                <div className="kpi-label">Pendientes</div>
              </div>
            </div>
            <div className="st-kpi-card critical">
              <div className="kpi-icon"><i className="fas fa-fire"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.criticalOpen}</div>
                <div className="kpi-label">Alta/Crítica Abiertos</div>
              </div>
            </div>
            <div className="st-kpi-card resolved">
              <div className="kpi-icon"><i className="fas fa-check-circle"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.resolvedToday}</div>
                <div className="kpi-label">Resueltos Hoy</div>
              </div>
            </div>
            <div className="st-kpi-card response">
              <div className="kpi-icon"><i className="fas fa-bolt"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">
                  {kpis.slaComplianceRatio === null ? '—' : `${kpis.slaComplianceRatio}%`}
                  {kpis.slaConsideredCount && kpis.slaConsideredCount > 0 ? ` (${kpis.slaPassCount}/${kpis.slaConsideredCount})` : ''}
                </div>
                <div className="kpi-label">Ratio de cumplimiento de SLA</div>
              </div>
            </div>
          </div>
        )}

        {/* Split layout - 2 columns below header */}
        <div className="st-admin-split">
          {/* LEFT — Filter + Ticket List */}
          <div className="st-admin-list-panel">
            {/* Filter */}
            <SupportTicketsFilterForm />
            
            <div className="ticket-filter-tabs">
              {FILTER_TABS.map(tab => (
                <button key={tab.key}
                  className={`ticket-filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
                  onClick={() => this.setState({ activeFilter: tab.key })}>
                  {tab.label}
                  {tab.key !== 'ALL' && counts[tab.key] > 0 &&
                    <span style={{ marginLeft: 4 }}>({counts[tab.key]})</span>
                  }
                </button>
              ))}
            </div>

            {error ? (
              <ErrorMessage message={error.message} />
            ) : !supporttickets || loading ? (
              <LoadingProgress />
            ) : (
              <div className="st-admin-ticket-list">
                {filteredTickets.length > 0
                  ? filteredTickets.map(ticket => this.renderTicketCard(ticket))
                  : (
                    <div className="st-admin-empty">
                      <i className="fas fa-inbox"></i>
                      <p>No hay tickets {activeFilter !== 'ALL' ? `con estado "${STATUS_LABELS[activeFilter]}"` : ''}</p>
                    </div>
                  )
                }
              </div>
            )}
          </div>

          {/* RIGHT — Summary Panel */}
          {supporttickets && !loading && this.renderSummaryPanel()}
        </div>

        {/* Confirm close modal */}
        <Modal isOpen={this.state.closeConfirmShown}
          toggle={() => this.setState({ closeConfirmShown: false })}>
          <ModalHeader toggle={() => this.setState({ closeConfirmShown: false })}>
            Confirmar Cierre
          </ModalHeader>
          <ModalBody>
            <p>¿Estás seguro de cerrar el ticket #{this.state.closeId}?</p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" className="mr-1" onClick={this.confirmClose}>Sí</Button>
            <Button color="secondary" onClick={() => this.setState({ closeConfirmShown: false })}>No</Button>
          </ModalFooter>
        </Modal>

        {/* Confirm delete modal */}
        <Modal isOpen={this.state.deleteConfirmShown}
          toggle={() => this.setState({ deleteConfirmShown: false })}>
          <ModalHeader toggle={() => this.setState({ deleteConfirmShown: false })}>
            Confirmar Eliminación
          </ModalHeader>
          <ModalBody>
            <p>¿Estás seguro de eliminar permanentemente el ticket #{this.state.deleteId}?</p>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={this.confirmDelete}>Eliminar</Button>
            <Button color="secondary" onClick={() => this.setState({ deleteConfirmShown: false })}>Cancelar</Button>
          </ModalFooter>
        </Modal>

        {/* Ticket detail modal */}
        {this.renderDetailModal()}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  supporttickets: state.supporttickets.supporttickets,
  supportticket: state.supporttickets.supportticket,
  error: state.supporttickets.error,
  role: state.auth.role,
  type: state.auth.type,
  loading: state.supporttickets.loading,
  supportticketNeedRefresh: state.supporttickets.supportticketNeedRefresh,
  form: state.form,
  slas: state.slas.slas
})

const mapDispatchToProps = dispatch => ({
  fetchSupportTickets: bindActionCreators(fetchSupportTickets, dispatch),
  fetchSupportTicket: bindActionCreators(fetchSupportTicket, dispatch),
  closeTicketSupport: bindActionCreators(closeTicketSupport, dispatch),
  reviewTicketSupport: bindActionCreators(reviewTicketSupport, dispatch),
  resolveTicketSupport: bindActionCreators(resolveTicketSupport, dispatch),
  incidenceTicketSupport: bindActionCreators(incidenceTicketSupport, dispatch),
  repairTicketSupport: bindActionCreators(repairTicketSupport, dispatch),
  deleteSupportTicket: bindActionCreators(deleteSupportTicket, dispatch),
  fetchSLAs: bindActionCreators(require('../actions/slas').fetchSLAs, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(SupportTicketsView)
